import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { formatVisits, formatVisitors, formatDayVisits, formatDayVisitsPerVisitorRatio, isLowSignalQuality } from '../utils/formatMetrics';
import './LocationSummaryModal.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const LocationSummaryModal = ({ isOpen, onClose, originsData, hexData, selectedHex, analysisName }) => {
  // Calculate metrics
  const metrics = useMemo(() => {
    if (!originsData || !hexData || !selectedHex) return null;

    // Sort origins by visitor count (total_occurences) descending
    const sortedOrigins = [...originsData.origins].sort((a, b) =>
      (b.total_occurences || 0) - (a.total_occurences || 0)
    );

    const totalVisitors = sortedOrigins.reduce((sum, o) => sum + (o.total_occurences || 0), 0);

    // Calculate cumulative percentages to find 50%, 70%, 90% thresholds
    let cumulative = 0;
    let threshold50 = 0;
    let threshold70 = 0;
    let threshold90 = 0;
    let top70Origins = [];
    let population50 = 0;
    let population70 = 0;
    let population90 = 0;

    sortedOrigins.forEach((origin) => {
      const visitors = origin.total_occurences || 0;
      const population = origin.population || 0;
      cumulative += visitors;
      const percentage = (cumulative / totalVisitors) * 100;

      if (threshold50 === 0 && percentage >= 50) {
        threshold50 = sortedOrigins.indexOf(origin) + 1;
        population50 = sortedOrigins.slice(0, threshold50).reduce((sum, o) => sum + (o.population || 0), 0);
      }
      if (threshold70 === 0 && percentage >= 70) {
        threshold70 = sortedOrigins.indexOf(origin) + 1;
        top70Origins = sortedOrigins.slice(0, threshold70);
        population70 = top70Origins.reduce((sum, o) => sum + (o.population || 0), 0);
      }
      if (threshold90 === 0 && percentage >= 90) {
        threshold90 = sortedOrigins.indexOf(origin) + 1;
        population90 = sortedOrigins.slice(0, threshold90).reduce((sum, o) => sum + (o.population || 0), 0);
      }
    });

    // Calculate average visits and visitors per hex
    const hexesWithData = hexData.hexagons.filter(h => h.devices > 0 || h.uniques > 0);
    const totalVisits = hexData.hexagons.reduce((sum, h) => sum + (h.devices || 0), 0);
    const totalUniqueVisitors = hexData.hexagons.reduce((sum, h) => sum + (h.uniques || 0), 0);
    const avgVisitsPerHex = hexesWithData.length > 0 ? totalVisits / hexesWithData.length : 0;
    const avgVisitorsPerHex = hexesWithData.length > 0 ? totalUniqueVisitors / hexesWithData.length : 0;

    // Calculate weighted demographics for top 70% origins
    const totalTop70Visitors = top70Origins.reduce((sum, o) => sum + (o.total_occurences || 0), 0);

    const demographics = {
      lifeStage: {
        'Young Pre-Parental': 0,
        'Pre School Families': 0,
        'School Families': 0,
        'Mature Families': 0,
        'Empty Nester': 0,
        'Retired': 0
      },
      affluence: {
        'Struggling': 0,
        'Getting by': 0,
        'Managing': 0,
        'Comfortable': 0,
        'Affluent': 0
      },
      socialGrade: {
        'AB': 0,
        'C1': 0,
        'C2': 0,
        'DE': 0
      },
      tenure: {
        'Owned Outright': 0,
        'Mortgage/Shared': 0,
        'Social Rented': 0,
        'Private Rented': 0
      },
      employment: {
        'Full-Time': 0,
        'Part-Time': 0,
        'Self-Employed (with employees)': 0,
        'Self-Employed (solo)': 0,
        'Unemployed': 0,
        'Student': 0,
        'Retired': 0,
        'Home Maker': 0,
        'Long-Term Sick/Disabled': 0,
        'Other': 0
      },
      education: {
        'No Qualifications': 0,
        'Level 1': 0,
        'Level 2': 0,
        'Apprenticeship': 0,
        'Level 3': 0,
        'Level 4+': 0
      }
    };

    top70Origins.forEach(origin => {
      const weight = (origin.total_occurences || 0) / totalTop70Visitors;

      // Life Stage and Affluence use dominant values (categorical)
      // Origins data only contains the dominant category, not individual breakdowns
      if (origin.life_stage && demographics.lifeStage[origin.life_stage] !== undefined) {
        demographics.lifeStage[origin.life_stage] += weight;
      }
      if (origin.affluence && demographics.affluence[origin.affluence] !== undefined) {
        demographics.affluence[origin.affluence] += weight;
      }

      // Social Grade - use actual population values if available
      const totalPop = origin.tot_pop || 1;
      if (origin.AB_pop !== undefined) {
        demographics.socialGrade['AB'] += weight * (origin.AB_pop / totalPop);
      }
      if (origin.C1_pop !== undefined) {
        demographics.socialGrade['C1'] += weight * (origin.C1_pop / totalPop);
      }
      if (origin.C2_pop !== undefined) {
        demographics.socialGrade['C2'] += weight * (origin.C2_pop / totalPop);
      }
      if (origin.DE_pop !== undefined) {
        demographics.socialGrade['DE'] += weight * (origin.DE_pop / totalPop);
      }

      // Tenure - use actual property values if available
      const totalProperties = origin.tot_properties || 1;
      if (origin.owned_outright !== undefined) {
        demographics.tenure['Owned Outright'] += weight * (origin.owned_outright / totalProperties);
      }
      if (origin.mortgage_shared !== undefined) {
        demographics.tenure['Mortgage/Shared'] += weight * (origin.mortgage_shared / totalProperties);
      }
      if (origin.social_rented !== undefined) {
        demographics.tenure['Social Rented'] += weight * (origin.social_rented / totalProperties);
      }
      if (origin.private_rented !== undefined) {
        demographics.tenure['Private Rented'] += weight * (origin.private_rented / totalProperties);
      }

      // Employment - use actual employment values if available
      // First calculate sum of employment fields for this postcode
      const employmentFields = [
        origin.full_time || 0,
        origin.part_time || 0,
        origin.self_employed_with_employees || 0,
        origin.self_employed_solo || 0,
        origin.unemployed_available || 0,
        origin.student_in_employment || 0,
        origin.student_unemployed_available || 0,
        origin.inactive_retired || 0,
        origin.inactive_student || 0,
        origin.inactive_looking_after_home || 0,
        origin.inactive_lts_disabled || 0,
        origin.inactive_other || 0
      ];
      const totalEmployment = employmentFields.reduce((sum, val) => sum + val, 0);

      if (totalEmployment > 0) {
        if (origin.full_time !== undefined) {
          demographics.employment['Full-Time'] += weight * (origin.full_time / totalEmployment);
        }
        if (origin.part_time !== undefined) {
          demographics.employment['Part-Time'] += weight * (origin.part_time / totalEmployment);
        }
        if (origin.self_employed_with_employees !== undefined) {
          demographics.employment['Self-Employed (with employees)'] += weight * (origin.self_employed_with_employees / totalEmployment);
        }
        if (origin.self_employed_solo !== undefined) {
          demographics.employment['Self-Employed (solo)'] += weight * (origin.self_employed_solo / totalEmployment);
        }
        if (origin.unemployed_available !== undefined) {
          demographics.employment['Unemployed'] += weight * (origin.unemployed_available / totalEmployment);
        }
        if (origin.student_in_employment !== undefined || origin.student_unemployed_available !== undefined || origin.inactive_student !== undefined) {
          demographics.employment['Student'] += weight * ((origin.student_in_employment || 0) + (origin.student_unemployed_available || 0) + (origin.inactive_student || 0)) / totalEmployment;
        }
        if (origin.inactive_retired !== undefined) {
          demographics.employment['Retired'] += weight * (origin.inactive_retired / totalEmployment);
        }
        if (origin.inactive_looking_after_home !== undefined) {
          demographics.employment['Home Maker'] += weight * (origin.inactive_looking_after_home / totalEmployment);
        }
        if (origin.inactive_lts_disabled !== undefined) {
          demographics.employment['Long-Term Sick/Disabled'] += weight * (origin.inactive_lts_disabled / totalEmployment);
        }
        if (origin.inactive_other !== undefined) {
          demographics.employment['Other'] += weight * (origin.inactive_other / totalEmployment);
        }
      }

      // Education - use actual education values if available
      const educationFields = [
        origin.no_qualifications || 0,
        origin.level_1 || 0,
        origin.level_2 || 0,
        origin.apprenticeship || 0,
        origin.level_3 || 0,
        origin.level_4 || 0
      ];
      const totalEducation = educationFields.reduce((sum, val) => sum + val, 0);

      if (totalEducation > 0) {
        if (origin.no_qualifications !== undefined) {
          demographics.education['No Qualifications'] += weight * (origin.no_qualifications / totalEducation);
        }
        if (origin.level_1 !== undefined) {
          demographics.education['Level 1'] += weight * (origin.level_1 / totalEducation);
        }
        if (origin.level_2 !== undefined) {
          demographics.education['Level 2'] += weight * (origin.level_2 / totalEducation);
        }
        if (origin.apprenticeship !== undefined) {
          demographics.education['Apprenticeship'] += weight * (origin.apprenticeship / totalEducation);
        }
        if (origin.level_3 !== undefined) {
          demographics.education['Level 3'] += weight * (origin.level_3 / totalEducation);
        }
        if (origin.level_4 !== undefined) {
          demographics.education['Level 4+'] += weight * (origin.level_4 / totalEducation);
        }
      }
    });

    // Convert to percentages
    Object.keys(demographics.lifeStage).forEach(key => {
      demographics.lifeStage[key] *= 100;
    });
    Object.keys(demographics.affluence).forEach(key => {
      demographics.affluence[key] *= 100;
    });
    Object.keys(demographics.socialGrade).forEach(key => {
      demographics.socialGrade[key] *= 100;
    });
    Object.keys(demographics.tenure).forEach(key => {
      demographics.tenure[key] *= 100;
    });
    Object.keys(demographics.employment).forEach(key => {
      demographics.employment[key] *= 100;
    });
    Object.keys(demographics.education).forEach(key => {
      demographics.education[key] *= 100;
    });

    // Calculate spend data for top 70% audience
    const spendData = { totalSpend: {}, avgSpendPerPerson: {} };
    let totalTop70Population = 0;

    top70Origins.forEach(origin => {
      if (origin.spend_by_pcode_by_category && origin.population) {
        totalTop70Population += origin.population;
        Object.entries(origin.spend_by_pcode_by_category).forEach(([category, spend]) => {
          spendData.totalSpend[category] = (spendData.totalSpend[category] || 0) + spend;
        });
      }
    });

    // Calculate average spend per person across all categories
    if (totalTop70Population > 0) {
      Object.keys(spendData.totalSpend).forEach(category => {
        spendData.avgSpendPerPerson[category] = spendData.totalSpend[category] / totalTop70Population;
      });
    }

    // Get top 10 postcodes with their individual spend data
    const top10PostcodesWithSpend = sortedOrigins.slice(0, 10).map(origin => ({
      postcode_sector: origin.postcode_sector,
      total_occurences: origin.total_occurences,
      life_stage: origin.life_stage,
      affluence: origin.affluence,
      imd_decile: origin.imd_decile,
      spend_by_category: origin.spend_by_pcode_by_category || {}
    }));

    // Get all unique spend categories from top 10 postcodes
    const spendCategories = new Set();
    top10PostcodesWithSpend.forEach(postcode => {
      Object.keys(postcode.spend_by_category).forEach(category => {
        spendCategories.add(category);
      });
    });

    return {
      selectedHexVisits: selectedHex.devices || 0, // Raw devices for calculation
      selectedHexVisitors: selectedHex.uniques || 0, // Raw uniques for calculation
      selectedHexVisitsFormatted: formatVisits(selectedHex.devices || 0), // Extrapolated and formatted
      selectedHexDayVisitsFormatted: formatDayVisits(selectedHex.devices || 0), // Extrapolated and formatted
      selectedHexVisitorsFormatted: formatVisitors(selectedHex.uniques || 0), // Extrapolated and formatted
      selectedHexDayVisitsPerVisitorRatio: formatDayVisitsPerVisitorRatio(selectedHex.devices || 0, selectedHex.uniques || 0),
      selectedHexLowSignalQuality: isLowSignalQuality(selectedHex.devices || 0, selectedHex.uniques || 0),
      avgVisitsPerHex,
      avgVisitorsPerHex,
      totalPostcodes: sortedOrigins.length,
      threshold50,
      threshold70,
      threshold90,
      population50,
      population70,
      population90,
      top10Postcodes: sortedOrigins.slice(0, 10),
      demographics,
      spendData,
      top10PostcodesWithSpend,
      spendCategories: Array.from(spendCategories).sort()
    };
  }, [originsData, hexData, selectedHex]);

  if (!isOpen || !metrics) return null;

  const createChartData = (data, label) => ({
    labels: Object.keys(data),
    datasets: [{
      label,
      data: Object.values(data),
      backgroundColor: 'rgba(102, 126, 234, 0.6)',
      borderColor: 'rgba(102, 126, 234, 1)',
      borderWidth: 1
    }]
  });

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.parsed.y.toFixed(1)}%`
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: (value) => `${value}%`
        }
      },
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 45
        }
      }
    }
  };

  const spendChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: (context) => `£${Math.round(context.parsed.y).toLocaleString()}`
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => `£${Math.round(value).toLocaleString()}`
        }
      },
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 45
        }
      }
    }
  };

  // Export CSV function
  const exportCSV = () => {
    if (!metrics) return;

    const csvRows = [];

    // Header
    csvRows.push('Location Summary Report');
    csvRows.push(`Hex ID,${selectedHex.hex_id}`);
    csvRows.push('');

    // Section 1: Visits and Visitors
    csvRows.push('Visits and Visitors');
    csvRows.push('Metric,Selected Hex,Average per Hex');
    csvRows.push(`Visits,${selectedHex.devices},${metrics.avgVisitsPerHex.toFixed(2)}`);
    csvRows.push(`Day Visits,${metrics.selectedHexDayVisitsFormatted},`);
    csvRows.push(`Visitors,${selectedHex.uniques},${metrics.avgVisitorsPerHex.toFixed(2)}`);
    csvRows.push(`Day Visits/Visitor Ratio,${metrics.selectedHexDayVisitsPerVisitorRatio},`);
    csvRows.push('');

    // Section 2: Catchment Postcodes
    csvRows.push('Catchment Postcodes');
    csvRows.push('Metric,Value');
    csvRows.push(`Total Postcodes,${metrics.totalPostcodes}`);
    csvRows.push(`50% of Visitors from,${metrics.threshold50} postcodes (${metrics.population50.toLocaleString()} people)`);
    csvRows.push(`70% of Visitors from,${metrics.threshold70} postcodes (${metrics.population70.toLocaleString()} people)`);
    csvRows.push(`90% of Visitors from,${metrics.threshold90} postcodes (${metrics.population90.toLocaleString()} people)`);
    csvRows.push('');

    // Top 10 Postcodes
    csvRows.push('Top 10 Postcodes');
    csvRows.push('Rank,Postcode,Visitors,Life Stage,Affluence,IMD Decile');
    metrics.top10Postcodes.forEach((origin, index) => {
      csvRows.push(`${index + 1},${origin.postcode_sector},${origin.total_occurences},${origin.life_stage || 'N/A'},${origin.affluence || 'N/A'},${origin.imd_decile || 'N/A'}`);
    });
    csvRows.push('');

    // Spend of Top 10 Postcodes
    if (metrics.top10PostcodesWithSpend && metrics.top10PostcodesWithSpend.length > 0 && metrics.spendCategories && metrics.spendCategories.length > 0) {
      csvRows.push('Spend of Top 10 Postcodes');
      const headerRow = ['Postcode', ...metrics.spendCategories.map(cat => `${cat} (£)`)];
      csvRows.push(headerRow.join(','));
      metrics.top10PostcodesWithSpend.forEach(postcode => {
        const row = [
          postcode.postcode_sector,
          ...metrics.spendCategories.map(cat => (postcode.spend_by_category[cat] || 0).toFixed(2))
        ];
        csvRows.push(row.join(','));
      });
      csvRows.push('');
    }

    // Demographics for Top 70% Audience
    csvRows.push('Demographics - Top 70% Audience');
    csvRows.push('');

    csvRows.push('Life Stage Distribution');
    csvRows.push('Life Stage,Percentage');
    Object.entries(metrics.demographics.lifeStage).forEach(([stage, percent]) => {
      if (percent > 0) {
        csvRows.push(`${stage},${percent.toFixed(2)}`);
      }
    });
    csvRows.push('');

    csvRows.push('Affluence Distribution');
    csvRows.push('Affluence Level,Percentage');
    Object.entries(metrics.demographics.affluence).forEach(([level, percent]) => {
      if (percent > 0) {
        csvRows.push(`${level},${percent.toFixed(2)}`);
      }
    });
    csvRows.push('');

    csvRows.push('Social Grade Distribution');
    csvRows.push('Social Grade,Percentage');
    Object.entries(metrics.demographics.socialGrade).forEach(([grade, percent]) => {
      if (percent > 0) {
        csvRows.push(`${grade},${percent.toFixed(2)}`);
      }
    });
    csvRows.push('');

    csvRows.push('Tenure Distribution');
    csvRows.push('Tenure Type,Percentage');
    Object.entries(metrics.demographics.tenure).forEach(([type, percent]) => {
      if (percent > 0) {
        csvRows.push(`${type},${percent.toFixed(2)}`);
      }
    });
    csvRows.push('');

    csvRows.push('Employment Distribution');
    csvRows.push('Employment Status,Percentage');
    Object.entries(metrics.demographics.employment).forEach(([status, percent]) => {
      if (percent > 0) {
        csvRows.push(`${status},${percent.toFixed(2)}`);
      }
    });
    csvRows.push('');

    // Spend Data for Top 70% Audience
    if (metrics.spendData && Object.keys(metrics.spendData.totalSpend).length > 0) {
      csvRows.push('Total Spend by Category (Top 70% Audience)');
      csvRows.push('Category,Total Spend (£)');
      Object.entries(metrics.spendData.totalSpend).forEach(([category, spend]) => {
        csvRows.push(`${category},${spend.toFixed(2)}`);
      });
      csvRows.push('');

      csvRows.push('Average Spend per Person by Category (Top 70% Audience)');
      csvRows.push('Category,Avg Spend per Person (£)');
      Object.entries(metrics.spendData.avgSpendPerPerson).forEach(([category, spend]) => {
        csvRows.push(`${category},${spend.toFixed(2)}`);
      });
      csvRows.push('');
    }

    // Create blob and download
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const filename = analysisName
      ? `location_summary_${analysisName.replace(/[^a-z0-9]/gi, '_')}.csv`
      : `location_summary_${selectedHex.hex_id}.csv`;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="location-summary-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Location Summary Report{analysisName ? ` - ${analysisName}` : ''}</h2>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {/* Section 1: Visits and Visitors */}
          <div className="summary-section">
            <h3>Visits and Visitors</h3>
            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-label">Visitors in Selected Hex:</span>
                <span className="stat-value">{metrics.selectedHexVisitorsFormatted}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Visits in Selected Hex:</span>
                <span className="stat-value">{metrics.selectedHexVisitsFormatted}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Day Visits in Selected Hex:</span>
                <span className="stat-value">{metrics.selectedHexDayVisitsFormatted}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Avg Visitors per Hex:</span>
                <span className="stat-value">{metrics.avgVisitorsPerHex.toFixed(0).toLocaleString()}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Avg Visits per Hex:</span>
                <span className="stat-value">{metrics.avgVisitsPerHex.toFixed(0).toLocaleString()}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Day Visits/Visitor Ratio:</span>
                <span className="stat-value">{metrics.selectedHexDayVisitsPerVisitorRatio}</span>
              </div>
            </div>
            {metrics.selectedHexLowSignalQuality && (
              <p style={{ color: '#e67e22', fontSize: '0.85rem', fontStyle: 'italic', marginTop: '0.5rem' }}>
                Reduced confidence due to signal noise in this hex
              </p>
            )}
          </div>

          {/* Section 2: Postcodes */}
          <div className="summary-section">
            <h3>Catchment Postcodes</h3>
            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-label">Total Catchment Postcodes:</span>
                <span className="stat-value">{metrics.totalPostcodes}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Postcodes forming first 50%:</span>
                <span className="stat-value">{metrics.threshold50} postcodes ({metrics.population50.toLocaleString()} people)</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Postcodes forming first 70%:</span>
                <span className="stat-value">{metrics.threshold70} postcodes ({metrics.population70.toLocaleString()} people)</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Postcodes forming first 90%:</span>
                <span className="stat-value">{metrics.threshold90} postcodes ({metrics.population90.toLocaleString()} people)</span>
              </div>
            </div>

            <h4>Top 10 Postcodes</h4>
            <div className="postcodes-table-container">
              <table className="postcodes-table">
                <thead>
                  <tr>
                    <th>Postcode</th>
                    <th>Visitors</th>
                    <th>Life Stage</th>
                    <th>Affluence</th>
                    <th>IMD</th>
                    <th>Social Grade</th>
                    <th>Tenure</th>
                    <th>Employment</th>
                    <th>Education</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.top10Postcodes.map((postcode, index) => (
                    <tr key={index}>
                      <td className="postcode-cell">{postcode.postcode_sector}</td>
                      <td>{formatVisitors(postcode.total_occurences || 0)}</td>
                      <td>{postcode.life_stage || '-'}</td>
                      <td>{postcode.affluence || '-'}</td>
                      <td>{postcode.imd_decile || '-'}</td>
                      <td>{postcode.dominant_social_grade || '-'}</td>
                      <td>{postcode.dominant_tenure || '-'}</td>
                      <td>{postcode.dominant_employment || '-'}</td>
                      <td>{postcode.dominant_education || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {metrics.top10PostcodesWithSpend && metrics.top10PostcodesWithSpend.length > 0 && metrics.spendCategories && metrics.spendCategories.length > 0 && (
              <>
                <h4 style={{ marginTop: '2rem' }}>Spend of Top 10 Postcodes</h4>
                <div className="postcodes-table-container" style={{ overflowX: 'auto' }}>
                  <table className="postcodes-table">
                    <thead>
                      <tr>
                        <th>Postcode</th>
                        {metrics.spendCategories.map(category => (
                          <th key={category}>{category} (£)</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {metrics.top10PostcodesWithSpend.map((postcode) => (
                        <tr key={postcode.postcode_sector}>
                          <td><strong>{postcode.postcode_sector}</strong></td>
                          {metrics.spendCategories.map(category => (
                            <td key={category}>
                              £{Math.round(postcode.spend_by_category[category] || 0).toLocaleString()}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>

          {/* Section 3: Demographics */}
          <div className="summary-section">
            <h3>Demographics (Top 70% of Audience)</h3>
            <p className="section-subtitle">
              Based on postcodes providing the top 70% of visitors
            </p>

            <div className="charts-grid">
              <div className="chart-container">
                <h4>Life Stage Profile</h4>
                <div className="chart-wrapper">
                  <Bar data={createChartData(metrics.demographics.lifeStage, 'Life Stage')} options={chartOptions} />
                </div>
              </div>

              <div className="chart-container">
                <h4>Income Profile</h4>
                <div className="chart-wrapper">
                  <Bar data={createChartData(metrics.demographics.affluence, 'Income')} options={chartOptions} />
                </div>
              </div>

              <div className="chart-container">
                <h4>Social Grade</h4>
                <div className="chart-wrapper">
                  <Bar data={createChartData(metrics.demographics.socialGrade, 'Social Grade')} options={chartOptions} />
                </div>
              </div>

              <div className="chart-container">
                <h4>Tenure</h4>
                <div className="chart-wrapper">
                  <Bar data={createChartData(metrics.demographics.tenure, 'Tenure')} options={chartOptions} />
                </div>
              </div>

              <div className="chart-container">
                <h4>Employment</h4>
                <div className="chart-wrapper">
                  <Bar data={createChartData(metrics.demographics.employment, 'Employment')} options={chartOptions} />
                </div>
              </div>

              <div className="chart-container">
                <h4>Education</h4>
                <div className="chart-wrapper">
                  <Bar data={createChartData(metrics.demographics.education, 'Education')} options={chartOptions} />
                </div>
              </div>

              {metrics.spendData && Object.keys(metrics.spendData.totalSpend).length > 0 && (
                <>
                  <div className="chart-container">
                    <h4>Total Spend by Category</h4>
                    <div className="chart-wrapper">
                      <Bar data={createChartData(metrics.spendData.totalSpend, 'Total Spend')} options={spendChartOptions} />
                    </div>
                  </div>

                  <div className="chart-container">
                    <h4>Average Spend per Person by Category</h4>
                    <div className="chart-wrapper">
                      <Bar data={createChartData(metrics.spendData.avgSpendPerPerson, 'Avg Spend per Person')} options={spendChartOptions} />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={exportCSV}>Export CSV</button>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default LocationSummaryModal;
